import React from 'react';
import {
    Grid,
    Typography,
    TextField,
    Button,
    DialogContent,
    IconButton,
    MenuItem,
    FormControl,
    InputLabel,
    Select,
    Checkbox,
    ListItemText,
    FormControlLabel,
} from '@mui/material';
import { Formik, Form, Field, ErrorMessage, FieldArray } from 'formik';
import DraggableDialog from '../../../ui/draggableDialog';
import { ValidDataService } from '../../../../service/validDataService';
import { toast } from 'react-toastify';
import { isLocalDev } from '../../../../utils/environment';
import ClearIcon from '@mui/icons-material/Clear';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import validLookup from '../../../../utils/validLookup';
import * as Yup from 'yup';

const validationSchema = Yup.object().shape({
    name: Yup.string().required('Lab name is required'),
});

const initialValuesLab = {
    name: '',
    contactPerson: '',
    phone: '',
    email: '',
    fax: '',
    website: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    country: '',
    //specialties: ['both'],
    //turnaroundDays: 7,
    //rushAvailable: true,
    //rushDays: 3,
    status: validLookup.validStatus.active,
    notes: ''
};

const validLabForm = ({
    insert, remove, form
}) => (
    <Form>
        {
            form.values.validLabsData.map((value, index) => (
                <Grid container spacing={2} key={index} sx={{ mt: 1, mb: 1 }}>
                    <Grid item xs={12} md={3}>
                        <Field
                            as={TextField}
                            name={`validLabsData[${index}].name`}
                            label="Lab Name"
                            required
                            fullWidth
                            size="small"
                        />
                        <ErrorMessage name={`validLabsData[${index}].name`}>{message => <Typography color={'red'}>{message}</Typography>}</ErrorMessage>
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <Field
                            as={TextField}
                            name={`validLabsData[${index}].contactPerson`}
                            label="Contact Person"
                            fullWidth
                            size="small"
                        />
                    </Grid>
                    <Grid item xs={12} md={2}>
                        <Field
                            as={TextField}
                            name={`validLabsData[${index}].phone`}
                            label="Phone"
                            fullWidth
                            size="small"
                        />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <Field
                            as={TextField}
                            name={`validLabsData[${index}].email`}
                            label="Email"
                            fullWidth
                            size="small"
                        />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Field
                            as={TextField}
                            name={`validLabsData[${index}].address`}
                            label="Address"
                            fullWidth
                            size="small"
                        />
                    </Grid>
                    <Grid item xs={12} md={2}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Specialties</InputLabel>
                            <Field
                                as={Select}
                                name={`validLabsData[${index}].specialties`}
                                label="Specialties"
                                multiple
                                renderValue={(selected) => selected.join(', ')}
                            >
                                <MenuItem value="glasses">
                                    <Checkbox checked={form.values.validLabsData[index].specialties.includes('glasses')} />
                                    <ListItemText primary="Glasses" />
                                </MenuItem>
                                <MenuItem value="contacts">
                                    <Checkbox checked={form.values.validLabsData[index].specialties.includes('contacts')} />
                                    <ListItemText primary="Contacts" />
                                </MenuItem>
                                <MenuItem value="both">
                                    <Checkbox checked={form.values.validLabsData[index].specialties.includes('both')} />
                                    <ListItemText primary="Both" />
                                </MenuItem>
                            </Field>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} md={1}>
                        <Field
                            as={TextField}
                            name={`validLabsData[${index}].turnaroundDays`}
                            label="Days"
                            type="number"
                            fullWidth
                            size="small"
                        />
                    </Grid>
                    <Grid item xs={12} md={2}>
                        <FormControlLabel
                            control={
                                <Field
                                    as={Checkbox}
                                    name={`validLabsData[${index}].rushAvailable`}
                                    checked={form.values.validLabsData[index].rushAvailable}
                                />
                            }
                            label="Rush Available"
                        />
                    </Grid>
                    <Grid item xs={12} md={2}>
                        <Field
                            as={TextField}
                            name={`validLabsData[${index}].status`}
                            label="Status"
                            fullWidth
                            select
                            size="small"
                        >
                            <MenuItem value="active">Active</MenuItem>
                            <MenuItem value="inactive">Inactive</MenuItem>
                        </Field>
                    </Grid>
                    <Grid item xs={12} md={1}>
                        <IconButton
                            color='primary'
                            type="button"
                            onClick={() => insert(form.values.validLabsData.length, initialValuesLab)}
                        >
                            <AddCircleIcon />
                        </IconButton>
                        {form.values.validLabsData.length > 1 &&
                            <IconButton
                                type="button"
                                onClick={() => remove(index)}
                            >
                                <ClearIcon />
                            </IconButton>
                        }
                    </Grid>
                </Grid>
            ))}
        <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={!form.isValid || form.isSubmitting}
        >
            {form.isSubmitting ? 'Creating...' : 'Create'}
        </Button>
    </Form>
);

export default function ValidLabInputForm({ open, onClose, onRecordAdded }) {

    const handleSubmit = async (values) => {
        try {
            console.log('Submitting values:', values);
            const response = await ValidDataService.addValidLab({ ...values });
            const { code, message, data } = response;

            if (code === 0) {
                toast.success(message);
                onClose();
                onRecordAdded(data);
            } else {
                toast.error(message);
            }
        } catch (error) {
            if (isLocalDev) {
                console.log(error);
            }
            onRecordAdded([]);
            toast.error('Failed to add record');
        }
    };

    return (
        <DraggableDialog title={"Add Lab"} onClose={onClose} open={open} width={'md'}>
            <DialogContent>
                <Formik
                    initialValues={initialValuesLab}
                    validationSchema={validationSchema}
                    onSubmit={handleSubmit}
                >
                    {({ isSubmitting, isValid }) => (
                        <Form>
                            <Grid container spacing={2} sx={{ mt: 1, mb: 1 }}>
                                <Grid item xs={12} md={6}>
                                    <Field
                                        as={TextField}
                                        name='name'
                                        label="Lab Name"
                                        required
                                        fullWidth
                                        size="small"
                                    />
                                    <ErrorMessage name='name'>{message => <Typography color={'red'}>{message}</Typography>}</ErrorMessage>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Field
                                        as={TextField}
                                        name={`contactPerson`}
                                        label="Contact Person"
                                        fullWidth
                                        size="small"
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <Field
                                        as={TextField}
                                        name={`phone`}
                                        label="Phone"
                                        fullWidth
                                        size="small"
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <Field
                                        as={TextField}
                                        name={`email`}
                                        label="Email"
                                        fullWidth
                                        size="small"
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <Field
                                        as={TextField}
                                        name={`fax`}
                                        label="Fax"
                                        fullWidth
                                        size="small"
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Field
                                        as={TextField}
                                        name={`address`}
                                        label="Address"
                                        fullWidth
                                        size="small"
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Field
                                        as={TextField}
                                        name={`city`}
                                        label="City"
                                        fullWidth
                                        size="small"
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <Field
                                        as={TextField}
                                        name={`state`}
                                        label="State"
                                        fullWidth
                                        size="small"
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <Field
                                        as={TextField}
                                        name={`zip`}
                                        label="Postal Code"
                                        fullWidth
                                        size="small"
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <Field
                                        as={TextField}
                                        name={`country`}
                                        label="Country"
                                        fullWidth
                                        size="small"
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <Field
                                        as={TextField}
                                        name={`website`}
                                        label="Website"
                                        fullWidth
                                        size="small"
                                    />
                                </Grid>
                                {/* <Grid item xs={12} md={2}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Specialties</InputLabel>
                                    <Field
                                        as={Select}
                                        name={`specialties`}
                                        label="Specialties"
                                        multiple
                                        renderValue={(selected) => selected.join(', ')}
                                    >
                                        <MenuItem value="glasses">
                                            <Checkbox checked={form.values.validLabsData[index].specialties.includes('glasses')} />
                                            <ListItemText primary="Glasses" />
                                        </MenuItem>
                                        <MenuItem value="contacts">
                                            <Checkbox checked={form.values.validLabsData[index].specialties.includes('contacts')} />
                                            <ListItemText primary="Contacts" />
                                        </MenuItem>
                                        <MenuItem value="both">
                                            <Checkbox checked={form.values.validLabsData[index].specialties.includes('both')} />
                                            <ListItemText primary="Both" />
                                        </MenuItem>
                                    </Field>
                                </FormControl>
                            </Grid> */}
                                {/* <Grid item xs={12} md={1}>
                                <Field
                                    as={TextField}
                                    name={`validLabsData[${index}].turnaroundDays`}
                                    label="Days"
                                    type="number"
                                    fullWidth
                                    size="small"
                                />
                            </Grid> */}
                                {/* <Grid item xs={12} md={2}>
                                <FormControlLabel
                                    control={
                                        <Field
                                            as={Checkbox}
                                            name={`validLabsData[${index}].rushAvailable`}
                                            checked={form.values.validLabsData[index].rushAvailable}
                                        />
                                    }
                                    label="Rush Available"
                                />
                            </Grid> */}
                                <Grid item xs={12} md={2}>
                                    <Field
                                        as={TextField}
                                        name={`status`}
                                        label="Status"
                                        fullWidth
                                        select
                                        size="small"
                                    >
                                        <MenuItem value="active">Active</MenuItem>
                                        <MenuItem value="inactive">Inactive</MenuItem>
                                    </Field>
                                </Grid>
                                <Grid item xs={12}>
                                    <Button sx={{ mr: 1 }} type="submit" variant="contained" color="primary" disabled={isSubmitting || !isValid}>
                                        {isSubmitting ? 'Creating...' : 'Create'}
                                    </Button>
                                    <Button variant="outlined" onClick={onClose}>
                                        Cancel
                                    </Button>
                                </Grid>
                            </Grid>
                        </Form>
                    )}
                </Formik>
            </DialogContent>
        </DraggableDialog>
    );
};